import { Navigate, Outlet, useLocation } from 'react-router'
import Spinner from 'src/components/ui/Spinner'
import { useFetchCustomerDataQuery } from 'src/redux/api/rest/subscriptionApi'
import { useAppSelector } from 'src/utils/hooks/reduxHooks'
import { makeErrorToast } from 'src/utils/makeToast'
import { hasPremiumPageAccess } from 'src/utils/subscriptionAccess'

const subExpiredAllowedPaths = [
	'/success',
	'/cancel',
	'/teamSettings',
	'/accountSettings',
	'/billing',
]

export default function PrivateRoute({
	isAuth = false,
	groupAccess,
	userGroups,
	redirectPath = '/login',
	children,
	path,
	requiresPremium = false,
}: {
	isAuth?: boolean
	groupAccess: string[]
	userGroups?: string[] | null
	redirectPath?: string
	children?: any
	path: string
	requiresPremium?: boolean
}) {
	const location = useLocation()
	const subscriptionStatus = useAppSelector(
		(state) => state.settings.subscriptionStatus
	)
	const tenantId = useAppSelector(
		(state) => state.auth.userInfo?.currentCompanyTenantId
	)
	const {
		data: customerData,
		isLoading: isCustomerDataLoading,
		isFetching: isCustomerDataFetching,
		error: customerDataError,
	} = useFetchCustomerDataQuery(tenantId!, {
		skip: !requiresPremium || !tenantId,
	})

	if (!isAuth) {
		return <Navigate to={redirectPath} replace />
	}
	if (!groupAccess || !userGroups) {
		console.log('userGroups: ', userGroups)
		console.log('groupAccess: ', groupAccess)
		makeErrorToast('Error in user group')
		return <Navigate to={redirectPath} replace />
	}

	if (groupAccess?.some((r: string) => userGroups?.includes(r))) {
		if (
			subscriptionStatus === 'expired' &&
			!subExpiredAllowedPaths.includes(path)
		) {
			return <Navigate to="/billing" replace />
		}

		if (requiresPremium) {
			if (!tenantId) {
				return <Spinner />
			}

			if (isCustomerDataLoading || isCustomerDataFetching) {
				return <Spinner />
			}

			const hasPremiumAccess = hasPremiumPageAccess(
				customerData?.subscriptionData
			)

			if (customerDataError) {
				makeErrorToast('Unable to verify subscription status')
				return children ? children : <Outlet />
			}

			if (!hasPremiumAccess) {
				const fromPath = `${location.pathname}${location.search}`
				const from = encodeURIComponent(fromPath)
				return <Navigate to={`/upgradePlan?from=${from}`} replace />
			}
		}

		return children ? children : <Outlet />
	}

	return <Navigate to={'/'} replace />
}
